// IncomingSettingsPage.js
import React, { useState } from "react";

const defaultIncomingSettings = {
    // Schedule Job
    scheduleStatus: "enabled", // "enabled" | "disabled"
    cronExpression: "0 25 14 ? * *",
    provisioningContentArea: "",
    contentItemProvisioningCheck: "UPDATE_MATCHES_BY_NAME",
    gradeColumnProvisioningCheck: "UPDATE_MATCHES_BY_NAME",
    restKey: "",
    restSecret: "",

    // Course Copy
    includeExtendedDataDuringCopy: false,

    // Source Transfer Settings
    transferProtocol: "SFTP", // Local | FTP | SFTP | SCP | FTPS
    server: "",
    port: "22",
    username: "",
    password: "",
    path: "",

    // File Format
    fieldDelimiter: ",",
    fieldQualifier: "\"",
    dateFormat: "yyyy-MM-dd",
    courseFilterMask: "",

    // Default Visibility
    provisionedItemsVisibility: "visible", // "hidden" | "visible"

    // Grade Column Settings
    makeColumnsVisibleToStudents: true,
    includeColumnsInCalculations: true,
    useRunningTotal: false,
    hideDefaultTotalsClassicOnly: false,
    enableAnonymousGrading: false,
    defaultCalculationFunction: "Average"
};

const IncomingSettingsPage = ({ onSave }) => {
    const [settings, setSettings] = useState(defaultIncomingSettings);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState("");

    const update = (patch) => {
        setSettings((prev) => ({ ...prev, ...patch }));
    };

    const handleSave = async () => {
        setMessage("");
        setSaving(true);
        try {
            if (onSave) {
                await onSave(settings);
                setMessage("Incoming settings saved.");
            } else {
                console.log("[IncomingSettingsPage] settings:", settings);
                setMessage("Settings updated (no backend wired — logged to console).");
            }
        } catch (err) {
            console.error("Save incoming settings failed:", err);
            setMessage("Failed to save settings.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: "12px 0", fontSize: 13 }}>
            {/* Schedule Job */}
            <Section title="Schedule Job">
                <Row
                    label="Set Job Status"
                    control={
                        <div>
                            <label style={{ marginRight: 16 }}>
                                <input
                                    type="radio"
                                    name="scheduleStatus"
                                    value="enabled"
                                    checked={settings.scheduleStatus === "enabled"}
                                    onChange={() => update({ scheduleStatus: "enabled" })}
                                    style={{ marginRight: 4 }}
                                />
                                Enabled
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="scheduleStatus"
                                    value="disabled"
                                    checked={settings.scheduleStatus === "disabled"}
                                    onChange={() => update({ scheduleStatus: "disabled" })}
                                    style={{ marginRight: 4 }}
                                />
                                Disabled
                            </label>
                        </div>
                    }
                />

                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> CRON Schedule</span>}
                    control={
                        <>
                            <input
                                style={{ width: 260 }}
                                value={settings.cronExpression}
                                onChange={(e) =>
                                    update({ cronExpression: e.target.value })
                                }
                            />
                            <RowHelp>
                                Enter a valid CRON expression which is used for scheduling the job.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="Provisioning Content Area"
                    control={
                        <>
                            <input
                                style={{ width: 260 }}
                                value={settings.provisioningContentArea}
                                onChange={(e) =>
                                    update({ provisioningContentArea: e.target.value })
                                }
                            />
                            <RowHelp>
                                The default content area where gradable items will be created when
                                provisioned via the REST interface. If a content area is specified
                                in the JSON payload, the specified content area will override this
                                setting.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="Content Item Provisioning Check"
                    control={
                        <select
                            style={{ width: 260 }}
                            value={settings.contentItemProvisioningCheck}
                            onChange={(e) =>
                                update({ contentItemProvisioningCheck: e.target.value })
                            }
                        >
                            <option value="UPDATE_MATCHES_BY_NAME">
                                Update Matches By Name
                            </option>
                            <option value="ALWAYS_CREATE_NEW">
                                Always Create New
                            </option>
                        </select>
                    }
                />

                <Row
                    label="Grade Column Provisioning Check"
                    control={
                        <select
                            style={{ width: 260 }}
                            value={settings.gradeColumnProvisioningCheck}
                            onChange={(e) =>
                                update({ gradeColumnProvisioningCheck: e.target.value })
                            }
                        >
                            <option value="UPDATE_MATCHES_BY_NAME">
                                Update Matches By Name
                            </option>
                            <option value="ALWAYS_CREATE_NEW">
                                Always Create New
                            </option>
                        </select>
                    }
                />

                <Row
                    label="REST key (Ultra only)"
                    control={
                        <>
                            <input
                                style={{ width: 260 }}
                                type="password"
                                value={settings.restKey}
                                onChange={(e) => update({ restKey: e.target.value })}
                            />
                            <RowHelp>
                                Key registered with the public REST API.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="REST secret (Ultra only)"
                    control={
                        <>
                            <input
                                style={{ width: 260 }}
                                type="password"
                                value={settings.restSecret}
                                onChange={(e) => update({ restSecret: e.target.value })}
                            />
                            <RowHelp>
                                Secret registered with the public REST API.
                            </RowHelp>
                        </>
                    }
                />
            </Section>

            {/* Course Copy Settings */}
            <Section title="Course Copy Settings">
                <Row
                    label="Include extended data during the course copy process."
                    control={
                        <>
                            <label style={{ marginRight: 16 }}>
                                <input
                                    type="radio"
                                    name="includeExtended"
                                    value="yes"
                                    checked={settings.includeExtendedDataDuringCopy === true}
                                    onChange={() =>
                                        update({ includeExtendedDataDuringCopy: true })
                                    }
                                    style={{ marginRight: 4 }}
                                />
                                Yes
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="includeExtended"
                                    value="no"
                                    checked={settings.includeExtendedDataDuringCopy === false}
                                    onChange={() =>
                                        update({ includeExtendedDataDuringCopy: false })
                                    }
                                    style={{ marginRight: 4 }}
                                />
                                No
                            </label>
                            <RowHelp>
                                When copying a course, this option will enable the inclusion of
                                extended data from the source course onto the target course.
                            </RowHelp>
                        </>
                    }
                />
            </Section>

            {/* Source Transfer Settings */}
            <Section title="Source Transfer Settings">
                <Row
                    label="Receive input files via FTP"
                    control={
                        <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                            {["Local", "FTP", "SFTP", "SCP", "FTPS"].map((proto) => (
                                <label key={proto}>
                                    <input
                                        type="radio"
                                        name="transferProtocol"
                                        value={proto}
                                        checked={settings.transferProtocol === proto}
                                        onChange={() =>
                                            update({ transferProtocol: proto })
                                        }
                                        style={{ marginRight: 4 }}
                                    />
                                    {proto}
                                </label>
                            ))}
                        </div>
                    }
                />

                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> Server</span>}
                    control={
                        <input
                            style={{ width: 260 }}
                            value={settings.server}
                            onChange={(e) => update({ server: e.target.value })}
                        />
                    }
                />

                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> Port</span>}
                    control={
                        <input
                            style={{ width: 80 }}
                            value={settings.port}
                            onChange={(e) => update({ port: e.target.value })}
                        />
                    }
                />

                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> Username</span>}
                    control={
                        <input
                            style={{ width: 260 }}
                            value={settings.username}
                            onChange={(e) => update({ username: e.target.value })}
                        />
                    }
                />

                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> Password</span>}
                    control={
                        <input
                            style={{ width: 260 }}
                            type="password"
                            value={settings.password}
                            onChange={(e) => update({ password: e.target.value })}
                        />
                    }
                />

                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> Path</span>}
                    control={
                        <input
                            style={{ width: 260 }}
                            value={settings.path}
                            onChange={(e) => update({ path: e.target.value })}
                        />
                    }
                />
            </Section>

            {/* File Format */}
            <Section title="File Format">
                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> Field Delimiter</span>}
                    control={
                        <>
                            <input
                                style={{ width: 80 }}
                                value={settings.fieldDelimiter}
                                onChange={(e) =>
                                    update({ fieldDelimiter: e.target.value })
                                }
                            />
                            <RowHelp>
                                The field delimiter is used by the system to differentiate columns
                                in the extract file. For CSV format use comma (,).
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> Field Qualifier</span>}
                    control={
                        <>
                            <input
                                style={{ width: 80 }}
                                value={settings.fieldQualifier}
                                onChange={(e) =>
                                    update({ fieldQualifier: e.target.value })
                                }
                            />
                            <RowHelp>
                                Field qualifier is used to enclose a field that contains the text
                                qualifier or the delimiter. For CSV format use double quotes (").
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label={<span><span style={{ color: "#c00" }}>*</span> Format for date column</span>}
                    control={
                        <>
                            <input
                                style={{ width: 160 }}
                                value={settings.dateFormat}
                                onChange={(e) => update({ dateFormat: e.target.value })}
                            />
                            <RowHelp>
                                This format defines how the service should interpret the format for
                                incoming date values.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="Course Filter Mask"
                    control={
                        <>
                            <input
                                style={{ width: 260 }}
                                value={settings.courseFilterMask}
                                onChange={(e) =>
                                    update({ courseFilterMask: e.target.value })
                                }
                            />
                            <RowHelp>
                                Enter a regular expression for the pattern which should fully match
                                the course_id which are processed by the service. Leave blank for
                                all courses.
                            </RowHelp>
                        </>
                    }
                />
            </Section>

            {/* Default Visibility */}
            <Section title="Default Visibility of Provisioned Items">
                <Row
                    label=""
                    control={
                        <>
                            <label style={{ marginRight: 16 }}>
                                <input
                                    type="radio"
                                    name="provisionedVisibility"
                                    value="hidden"
                                    checked={
                                        settings.provisionedItemsVisibility === "hidden"
                                    }
                                    onChange={() =>
                                        update({
                                            provisionedItemsVisibility: "hidden"
                                        })
                                    }
                                    style={{ marginRight: 4 }}
                                />
                                Hidden
                            </label>
                            <label>
                                <input
                                    type="radio"
                                    name="provisionedVisibility"
                                    value="visible"
                                    checked={
                                        settings.provisionedItemsVisibility === "visible"
                                    }
                                    onChange={() =>
                                        update({
                                            provisionedItemsVisibility: "visible"
                                        })
                                    }
                                    style={{ marginRight: 4 }}
                                />
                                Visible
                            </label>
                            <RowHelp>
                                Set the visibility of the content items provisioned based on above
                                setting.
                            </RowHelp>
                        </>
                    }
                />
            </Section>

            {/* Grade Column Settings */}
            <Section title="Grade Column Settings">
                <Row
                    label="Make Grade Columns visible to Students?"
                    control={
                        <>
                            <input
                                type="checkbox"
                                checked={settings.makeColumnsVisibleToStudents}
                                onChange={(e) =>
                                    update({
                                        makeColumnsVisibleToStudents: e.target.checked
                                    })
                                }
                            />
                            <RowHelp>
                                Check if grade columns are supposed to be visible to students.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="Include Grade Columns in Calculations?"
                    control={
                        <>
                            <input
                                type="checkbox"
                                checked={settings.includeColumnsInCalculations}
                                onChange={(e) =>
                                    update({
                                        includeColumnsInCalculations: e.target.checked
                                    })
                                }
                            />
                            <RowHelp>
                                Check if grade columns are supposed to be included in
                                calculations.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="Use running total in calculations?"
                    control={
                        <>
                            <input
                                type="checkbox"
                                checked={settings.useRunningTotal}
                                onChange={(e) =>
                                    update({
                                        useRunningTotal: e.target.checked
                                    })
                                }
                            />
                            <RowHelp>
                                Check if running total is supposed to be calculated.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="Hide the default total and weighted total columns (Classic Only)?"
                    control={
                        <>
                            <input
                                type="checkbox"
                                checked={settings.hideDefaultTotalsClassicOnly}
                                onChange={(e) =>
                                    update({
                                        hideDefaultTotalsClassicOnly: e.target.checked
                                    })
                                }
                            />
                            <RowHelp>
                                Check if total and weighted total columns should be hidden.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="Enable Anonymous Grading"
                    control={
                        <>
                            <input
                                type="checkbox"
                                checked={settings.enableAnonymousGrading}
                                onChange={(e) =>
                                    update({
                                        enableAnonymousGrading: e.target.checked
                                    })
                                }
                            />
                            <RowHelp>
                                Enable if you want all new columns created in the Grade Center to
                                be automatically set as graded anonymously.
                            </RowHelp>
                        </>
                    }
                />

                <Row
                    label="Default Calculation Function (Ultra Courses only)"
                    control={
                        <>
                            <select
                                style={{ width: 160 }}
                                value={settings.defaultCalculationFunction}
                                onChange={(e) =>
                                    update({
                                        defaultCalculationFunction: e.target.value
                                    })
                                }
                            >
                                <option value="Average">Average</option>
                                <option value="Sum">Sum</option>
                                <option value="Min">Minimum</option>
                                <option value="Max">Maximum</option>
                            </select>
                            <RowHelp>
                                Select the default calculation function to be applied when
                                provisioning a calculated grade column using Grade Export.
                            </RowHelp>
                        </>
                    }
                />
            </Section>

            {/* Save actions */}
            <div
                style={{
                    marginTop: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 12
                }}
            >
                <button type="button" onClick={handleSave} disabled={saving}>
                    {saving ? "Saving…" : "Save Settings"}
                </button>
                {message && (
                    <span style={{ fontSize: 12, opacity: 0.8 }}>{message}</span>
                )}
            </div>
        </div>
    );
};

/* ---- small layout helpers ---- */

const Section = ({ title, children }) => (
    <div style={{ marginBottom: 20 }}>
        <div
            style={{
                fontWeight: 600,
                marginBottom: 6,
                marginTop: 6
            }}
        >
            {title}
        </div>
        <div
            style={{
                borderTop: "1px solid #eee",
                paddingTop: 8,
                display: "grid",
                rowGap: 8
            }}
        >
            {children}
        </div>
    </div>
);

const Row = ({ label, control }) => (
    <div
        style={{
            display: "grid",
            gridTemplateColumns: "220px minmax(200px, 640px)",
            columnGap: 16,
            alignItems: "flex-start"
        }}
    >
        <div style={{ fontSize: 12, fontWeight: 600, paddingTop: 4 }}>
            {label}
        </div>
        <div>{control}</div>
    </div>
);

const RowHelp = ({ children }) => (
    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{children}</div>
);

export default IncomingSettingsPage;
